/**
 * catalogueLinks.ts — the catalogue's filtered-view URLs, built in exactly one place.
 *
 * TD-F04 asks that a tool's primary category and each of its tags be a LINK into a catalogue view
 * filtered to that classification. Three surfaces now emit those links — the result card, the tool
 * page's Classification list, and (for paging) the catalogue's own pager — and the filter takes an
 * **id** while the reader needs a **word**. That combination is what makes a hand-written template
 * string a bad idea twice over: the id and the label have to travel together, and the encoding has to
 * be right for labels like `Finance & Ops` or `power automate`.
 *
 * So this module owns the shape of the URL, and no component composes one itself. It is pure — no
 * React, no router, no imports at all — which is the only reason `test/catalogueLinks.test.ts` can
 * assert the URLs at all: `packages/web` has no jsdom, so anything that must be tested has to be a
 * function rather than a component.
 *
 * Encoding is `URLSearchParams`, matching `Catalogue.tsx`'s pager rather than the hand-rolled
 * `encodeURIComponent` the tool page used to use. Both decode identically through `params.get`, but
 * only one of them is right about `&` and `=` inside a label without anyone having to remember.
 */

/** The catalogue route every link below points at. One constant so a route rename is one edit. */
const CATALOGUE_PATH = '/tools';

/** A tag, as both the API payloads carry it (`CatalogueToolSummary.tagLinks`). */
export interface CatalogueTagRef {
  tagId: string;
  label: string;
}

/** A category or department option, as `ToolRefData` carries it. */
export interface CatalogueRefOption {
  id: number;
  label: string;
}

function catalogueHref(params: URLSearchParams): string {
  const qs = params.toString();
  return `${CATALOGUE_PATH}${qs ? `?${qs}` : ''}`;
}

/**
 * The catalogue filtered to one tag — `/tools?tag=<id>&tagLabel=<label>`.
 *
 * The label rides along because the destination page titles itself with the tag (Will's "Catalogue
 * Page with tag query as title" diagram) and the id alone cannot spell it: the tag vocabulary is not
 * in the catalogue's refdata, so there would be nothing to look the name up in.
 *
 * That is safe here, and only here, because there is no tag CONTROL in the filter form (DS-F18 gives
 * the page a scope/category/department/builder/review/rating set and deliberately no tag picker). The
 * only ways `tag` can change are following another tag link or clearing the filter, and both replace
 * the pair — so the label cannot end up describing a tag that is no longer applied. `tagLabel` is
 * never forwarded to the API as a filter; the loader reads it back purely for the heading.
 */
export function catalogueTagHref(tag: CatalogueTagRef): string {
  const p = new URLSearchParams();
  p.set('tag', tag.tagId);
  p.set('tagLabel', tag.label);
  return catalogueHref(p);
}

/**
 * The catalogue filtered to one primary category — `/tools?category=<id>`, id only.
 *
 * Deliberately NOT `&categoryLabel=…`, which is where the symmetry with `catalogueTagHref` above
 * stops. The filter form HAS a category `<select>`, so a display-only label param would be stale the
 * moment somebody arrived through this link and then changed that dropdown: the URL would carry
 * `category=7&categoryLabel=Finance` and the heading would name a category the page is no longer
 * filtered to. `catalogueHeading` looks the name up from the refdata the page already fetched for the
 * dropdowns instead, which cannot disagree with the id because it is derived from it.
 *
 * The id is a `SMALLINT` reference-list key; a string is accepted so a caller holding the raw query
 * param does not have to round-trip it through `Number` to build a link back to itself.
 */
export function catalogueCategoryHref(categoryId: number | string): string {
  const p = new URLSearchParams();
  p.set('category', String(categoryId));
  return catalogueHref(p);
}

/**
 * The catalogue's `<h1>` for the filters currently applied.
 *
 * A filtered view says what it is filtered to, because a page of eight tools under the word
 * "Catalogue" reads as a catalogue of eight tools. Only the two TD-F04 classifications get a title —
 * scope, builder, review status and rating are all visible in the controls right below the heading,
 * whereas category and tag are the two a person can arrive at from somewhere else entirely.
 *
 * Tag wins when both are set: the tag is the narrower filter and the one the reader just clicked.
 *
 * Every fallback is `'Catalogue'` rather than a guess. An id with no matching refdata row is not
 * hypothetical — `catalogueLoader` treats a failed refdata fetch as non-fatal so the dropdowns
 * degrade instead of the page dying, which leaves `categories` empty while `category` is still set.
 * A heading that silently drops back to the generic word is honest about that; `Category: 7` or
 * `Category: undefined` would not be.
 */
export function catalogueHeading(args: {
  tag: string;
  tagLabel: string;
  category: string;
  categories: readonly CatalogueRefOption[];
}): string {
  if (args.tag && args.tagLabel) return `Tagged: ${args.tagLabel}`;
  if (args.category) {
    // String-compared: `category` is a raw query param, and the ids are numbers.
    const match = args.categories.find((c) => String(c.id) === args.category);
    if (match) return `Category: ${match.label}`;
  }
  return 'Catalogue';
}
